import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalComponent } from '../modal/modal.component';

export interface GridColumn {
  field: string;
  header: string;
  sortable?: boolean;
}

@Component({
  selector: 'app-admin-grid',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  templateUrl: './admin-grid.component.html',
  styleUrls: ['./admin-grid.component.scss'],
})
export class AdminGridComponent {
  @Input() title = 'Admin Grid';
  @Input() columns: GridColumn[] = [];
  @Input() searchable = true;
  @Input() addEnabled = true;
  @Input() entityLabel = 'Record';

  private _data: any[] = [];
  @Input() set data(value: any[]) {
    this._data = value || [];
    this.filteredData = [...this._data];
    this.applySearch();
  }
  get data(): any[] {
    return this._data;
  }

  @Input() loadLookups?: (row: any) => Promise<Record<string, any[]>>;
  @Output() rowClicked = new EventEmitter<any>();
  @Output() addClicked = new EventEmitter<any>();
  @Output() editClicked = new EventEmitter<any>();
  @Output() deleteClicked = new EventEmitter<any>();

  filteredData: any[] = [];
  searchTerm = '';
  sortField: string | null = null;
  sortAsc = true;

  modalVisible = false;
  modalMode: 'view' | 'edit' | 'add' | 'delete' = 'view';
  activeRow: any = null;
  lookupLists: Record<string, any[]> = {};

  applySearch(): void {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.filteredData = [...this._data];
      return;
    }
    this.filteredData = this._data.filter((item) =>
      Object.values(item).some((v) => String(v).toLowerCase().includes(term))
    );
  }

  onSort(field: string): void {
    if (this.sortField === field) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortField = field;
      this.sortAsc = true;
    }

    this.filteredData = [...this.filteredData].sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      if (valA < valB) return this.sortAsc ? -1 : 1;
      if (valA > valB) return this.sortAsc ? 1 : -1;
      return 0;
    });
  }

  onRowClick(row: any): void {
    this.modalMode = 'view';
    this.activeRow = { ...row };
    this.modalVisible = true;
    this.rowClicked.emit(row);
  }

  onAdd(): void {
    this.modalMode = 'add';
    const emptyTemplate = this.columns.reduce((acc, col) => {
      acc[col.field] = '';
      return acc;
    }, {} as any);

    this.activeRow = { ...emptyTemplate };
    this.lookupLists = {};
    this.modalVisible = true;
  }

  /** waits for lookup data before showing modal */
  async onEdit(row: any): Promise<void> {
    this.modalMode = 'edit';
    this.activeRow = { ...row };

    if (this.loadLookups) {
      try {
        this.lookupLists = await this.loadLookups(row);
      } catch (err) {
        console.error('Error loading lookups:', err);
        this.lookupLists = {};
      }
    }

    // show modal *after* lookupLists ready
    this.modalVisible = true;
  }

  onDelete(row: any): void {
    this.modalMode = 'delete';
    this.activeRow = { ...row };
    this.lookupLists = {};
    this.modalVisible = true;
  }

  handleModalConfirm(updatedData: any): void {
    switch (this.modalMode) {
      case 'add':
        this.addClicked.emit(updatedData);
        break;
      case 'edit':
        this.editClicked.emit(updatedData);
        break;
      case 'delete':
        this.deleteClicked.emit(updatedData);
        break;
    }
    this.modalVisible = false;
  }

  handleModalClose(): void {
    this.modalVisible = false;
  }
}